import { z } from 'zod';
import { CompanyResponseSchema } from './company.schemas';
import { OrganizationOnboardingStatusSchema } from './organization-onboarding.schemas';
import { refersTo } from '../common/refers-to';

// Operating model of a Standort. UPPER_CASE union codes per the
// domain-constants convention; the German display labels (Eigener Standort /
// Partnerunternehmen) live only in `@immo/i18n` and are mapped from these
// codes on the client.
export const OperatingModelSchema = z.enum(['OWN_BRANCH', 'PARTNER_COMPANY']);

export type OperatingModel = z.infer<typeof OperatingModelSchema>;

// Setup/onboarding status of a Standort. Lifecycle-managed (never set through
// the write body): a new subsidiary starts IN_SETUP and is marked COMPLETED
// once its onboarding finishes.
export const SetupStatusSchema = z.enum(['IN_SETUP', 'COMPLETED']);

export type SetupStatus = z.infer<typeof SetupStatusSchema>;

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

const optionalHttpUrl = z.preprocess(emptyToNull, HttpUrlSchema.nullable()).default(null);

// Lifecycle status of a Standort. ACTIVE Standorte are pickable everywhere;
// INACTIVE ones stay attached to existing records but drop out of pickers. The
// deactivate/reactivate transitions themselves are owned by IMM-132 — this slice
// only carries and renders the column (every Standort defaults to ACTIVE). The
// German display labels (aktiv / inaktiv) live only in `@immo/i18n`.
export const StandortStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);

export type StandortStatus = z.infer<typeof StandortStatusSchema>;

const optionalEmail = z.preprocess(emptyToNull, z.email().nullable()).default(null);

const optionalPhone = z.preprocess(emptyToNull, z.string().trim().max(30).nullable()).default(null);

const optionalDescription = z.preprocess(emptyToNull, z.string().trim().max(1000).nullable()).default(null);

// Write shape for a Standort's address, shared by create and update.
const OrganizationAddressBodySchema = z.object({
  street: z.string().trim().min(1).max(100),
  houseNumber: z.string().trim().min(1).max(20),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

const AddressSchema = z.object({
  street: z.string(),
  houseNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

// Leading digits of every Objekt-Nr. assigned at a Standort (IMM-551.1). Read
// only: assigned by the system in creation order from 100, immutable, and absent
// from every write body. The German label (Standort-Präfix) lives in `@immo/i18n`.
const ObjectNumberPrefixSchema = z.number().int();

export const CreateOrganizationPathParamsSchema = WorkspaceIdPathParamsSchema;

export const CreateOrganizationBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  // The parent Standort under which the new sub-Standort hangs. Null defaults to
  // the workspace's Hauptstandort, resolved server-side. A new Standort created
  // through this route is always a sub-Standort (`type: subsidiary`).
  parentId: z.uuid().nullable().default(null),
  email: optionalEmail,
  phone: optionalPhone,
  description: optionalDescription,
  address: OrganizationAddressBodySchema,
});

export type CreateOrganizationBody = z.infer<typeof CreateOrganizationBodySchema>;

export const CreateOrganizationResponseSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  name: z.string().min(1),
  type: z.literal('subsidiary'),
  parentId: refersTo('organization', z.uuid()).nullable(),
  status: StandortStatusSchema,
  email: z.string().nullable(),
  phone: z.string().nullable(),
  description: z.string().nullable(),
  objectNumberPrefix: ObjectNumberPrefixSchema,
  createdAt: z.iso.datetime(),
  address: AddressSchema,
});

export type CreateOrganizationResponse = z.infer<typeof CreateOrganizationResponseSchema>;

export const ListOrganizationTreePathParamsSchema = WorkspaceIdPathParamsSchema;

export const ListMyOrganizationsPathParamsSchema = WorkspaceIdPathParamsSchema;

export const ListWorkspaceOrganizationsPathParamsSchema = WorkspaceIdPathParamsSchema;

// The location-level profile carried on every organisation read (IMM-285).
// operatingModel/setupStatus are always present; companyId points at the
// operating company (null for an OWN_BRANCH location, which runs under the
// workspace's main company); internalWebsite is null until maintained. The
// firm's legal identity lives on the referenced Company, not here.
const OrganizationProfileResponseFields = {
  operatingModel: OperatingModelSchema,
  setupStatus: SetupStatusSchema,
  internalWebsite: z.string().nullable(),
  companyId: z.uuid().nullable(),
  // The lifecycle stage of this Standort's running partner onboarding, or null
  // when no onboarding record exists (every OWN_BRANCH location, and partner
  // locations before the onboarding is triggered). Surfaced read-only so the
  // Standorte list can show the stage label alongside the setup badge.
  onboardingStatus: OrganizationOnboardingStatusSchema.nullable(),
} as const;

export const WorkspaceOrganizationListItemSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  type: z.enum(['main', 'subsidiary']),
  // Parent Standort in the hierarchy; null for the Hauptstandort (the workspace's
  // single root). Drives the indented tree on Einstellungen → Standorte.
  parentId: refersTo('organization', z.uuid()).nullable(),
  status: StandortStatusSchema,
  email: z.string().nullable(),
  phone: z.string().nullable(),
  description: z.string().nullable(),
  // Members affiliated with this Standort and customer records assigned to it.
  // The affiliation/record substrate lands in IMM-20.3/IMM-20.4 — until then the
  // tree renders the columns with 0.
  memberCount: z.number().int().min(0),
  recordCount: z.number().int().min(0),
  objectNumberPrefix: ObjectNumberPrefixSchema,
  createdAt: z.iso.datetime(),
  address: AddressSchema,
  ...OrganizationProfileResponseFields,
  // Whether the caller can act on this Standort. Omitted/true everywhere the
  // list reflects the caller's own access (the full `list`/tree, every owner
  // view). `listMine` sets it to `false` on the ancestor Standorte it adds purely
  // to give an affiliated leaf its place in the tree — the location selector
  // renders those as non-selectable context rows.
  accessible: z.boolean().optional(),
});

export type WorkspaceOrganizationListItem = z.infer<typeof WorkspaceOrganizationListItemSchema>;

export const ListOrganizationTreeResponseSchema = z.object({
  data: z.array(WorkspaceOrganizationListItemSchema),
});

export type ListOrganizationTreeResponse = z.infer<typeof ListOrganizationTreeResponseSchema>;

export const ListMyOrganizationsResponseSchema = z.object({
  data: z.array(WorkspaceOrganizationListItemSchema),
});

export type ListMyOrganizationsResponse = z.infer<typeof ListMyOrganizationsResponseSchema>;

export const ListWorkspaceOrganizationsResponseSchema = z.object({
  data: z.array(WorkspaceOrganizationListItemSchema),
});

export type ListWorkspaceOrganizationsResponse = z.infer<typeof ListWorkspaceOrganizationsResponseSchema>;

// Org-addressed (top-level `/v1/organizations/:organizationId`, ADR 0029
// amendment): the workspace is derived from the organisation by the gate, not
// supplied in the URL, so the path schema carries only the organisation id.
export const GetWorkspaceOrganizationPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

// The detail read embeds the referenced operating Company (the partner firm's
// legal identity + contact block) on top of the list-item shape, so the
// Standort detail page can render it without a second lookup. `company` is null
// for an OWN_BRANCH location, which has no own Company. The LIST response keeps
// the bare list-item shape — list items never carry `company`.
export const GetWorkspaceOrganizationResponseSchema = WorkspaceOrganizationListItemSchema.extend({
  company: CompanyResponseSchema.nullable(),
});

export type GetWorkspaceOrganizationResponse = z.infer<typeof GetWorkspaceOrganizationResponseSchema>;

// Org-addressed (top-level `/v1/organizations/:organizationId`, ADR 0029
// amendment): workspace derived from the organisation by the gate.
export const UpdateWorkspaceOrganizationPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const UpdateWorkspaceOrganizationBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  // Reparent target. For a sub-Standort, null is coerced server-side to the
  // workspace's Hauptstandort (the tree stays single-rooted); for the
  // Hauptstandort itself a non-null parent is rejected — it must stay root.
  // Setting a Standort's parent to itself or a descendant is rejected server-side
  // with the cycle error.
  parentId: z.uuid().nullable(),
  email: optionalEmail,
  phone: optionalPhone,
  description: optionalDescription,
  address: OrganizationAddressBodySchema,
});

export type UpdateWorkspaceOrganizationBody = z.infer<typeof UpdateWorkspaceOrganizationBodySchema>;

export const UpdateWorkspaceOrganizationResponseSchema = WorkspaceOrganizationListItemSchema;

export type UpdateWorkspaceOrganizationResponse = z.infer<typeof UpdateWorkspaceOrganizationResponseSchema>;

// Org-addressed (top-level `/v1/organizations/:organizationId/profile`, ADR 0029
// amendment): workspace derived from the organisation by the gate.
export const UpdateOrganizationProfilePathParamsSchema = z.object({
  organizationId: z.uuid(),
});

// Write body for the location-level profile. `setupStatus` is omitted on
// purpose — it is lifecycle-managed, never set by the client; `companyId` is
// set when a partner company is linked (IMM-286/288), not here. The
// internalWebsite uses the emptyToNull pattern so an empty submission clears it.
export const UpdateOrganizationProfileBodySchema = z
  .object({
    operatingModel: OperatingModelSchema,
    internalWebsite: optionalHttpUrl,
  })
  .strict();

export type UpdateOrganizationProfileBody = z.infer<typeof UpdateOrganizationProfileBodySchema>;

export const UpdateOrganizationProfileResponseSchema = WorkspaceOrganizationListItemSchema;

export type UpdateOrganizationProfileResponse = z.infer<typeof UpdateOrganizationProfileResponseSchema>;

export const GetMyOrganizationPermissionsPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const GetMyOrganizationPermissionsResponseSchema = z.object({
  permissions: z.array(z.string().min(1)),
});

export type GetMyOrganizationPermissionsResponse = z.infer<typeof GetMyOrganizationPermissionsResponseSchema>;
